import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import ButtonHoverRender from '../../../Buttons/ButtonHover/ButtonHoverRender';
import ButtonHoverPrimary from '../../../Buttons/ButtonHover/ButtonHoverPrimary';
import VrGoggleSvg from '../../../SvgComponents/VrGoggleSvg';
import { setNewMapBounds } from '../../../../utils/setNewMapBounds';
import PlayButton from '../../../Buttons/PlayButton/PlayButton';
import ProgressBar from '../ProgressBar/ProgressBar';
import CameraControls from '../../../AFrame/components/CameraControls/CameraControls';
import { useTranslation } from 'react-i18next';

import './MainMediaControls.scss';

const MainMediaControls = props => {
  const {
    tier,
    tourPublished,
    primaryColor,
    closeShareModal,
    toggleVR,
    panoRotating,
    toggleAutoRotate,
    autoRotateDirection,
    toggleFullScreen,
    handlePlay,
    miniMapView,
    is360Pano,
    isVideo360,
    videoCanPlay,
    progress,
    duration,
    currentTime,
    playing,
    stops,
    stopIds,
    VRMode,
    viewOnly,
    buttonCount,
    showVRMode,
    staticImg,
    minimizePlayButton,
    currentStopId,
  } = props;
  const { t } = useTranslation();
  const mapView = miniMapView !== 'map';
  const [isMobile, setMobile] = useState(window.innerWidth < 1025);
  const [cameraControlsOpen, setCameraControlsOpen] = useState(false);

  const updateViewportType = () => {
    setMobile(window.innerWidth < 1025);
  };

  useEffect(() => {
    window.addEventListener('resize', updateViewportType);
    return () => window.removeEventListener('resize', updateViewportType);
  }, []);

  // close camera controls when stop changes
  useEffect(() => {
    setCameraControlsOpen(false);
  }, [currentStopId]);

  const toggleCameraControls = () => {
    setCameraControlsOpen(!cameraControlsOpen);
  };

  const controlsWrapperClasses = cx({
    'pano-ctrls-wrapper': true,
    starter: tier === 'starter',
    'map-view': mapView,
    hide: VRMode,
    'single-tour-btn': buttonCount === 1,
    'no-tour-btn': buttonCount,
  });

  const materialIcons = cx('material-icons', { active: playing });

  const direction =
    autoRotateDirection !== 'clockwise' ? 'rotateLeft' : 'rotateRight';
  const enabled = panoRotating ? 'Enabled' : 'Disabled';
  const autoRotateAriaLabel = t(`publicTour.ada.${direction}${enabled}`);

  return (
    <div className="pano-ctrls-container" role="region" aria-label={t('publicTour.ada.panoControls')}>
      {isVideo360 && videoCanPlay && (
        <div className="play-btn-tooltip__wrapper">
          <ButtonHoverRender
            render={({ onEnter, onLeave, hovering }) => (
              <PlayButton
                onEnter={onEnter}
                onLeave={onLeave}
                hovering={hovering}
                handleClick={handlePlay}
                color={primaryColor}
                playing={playing}
                progress={progress}
                currentTime={currentTime}
                duration={duration}
                backgroundColor="#fff"
                tier={tier}
                tooltip={{
                  text: t(
                    `publicTour.playButton.${playing ? 'pause' : 'play'}`
                  ),
                  node: 'pano-ctrl-btn.play-btn',
                  position: 'bottom',
                }}
                tooltipClass={`${
                  playing ? 'pause-360-video-tooltip' : 'play-360-video-tooltip'
                }`}
                isMobile={isMobile}
                minimize={minimizePlayButton}
              >
                <i aria-hidden="true" className={materialIcons}>
                  {playing ? 'pause' : 'play_arrow'}
                </i>
              </PlayButton>
            )}
          />
          <ProgressBar
            color={primaryColor}
            tier={tier}
            playing={playing}
            progress={progress}
          />
        </div>
      )}

      <div className="media-controls-section">
        <ul className={controlsWrapperClasses}>
          <React.Fragment>
            {viewOnly && (
              <React.Fragment>
                {tourPublished && (
                  <li>
                    <ButtonHoverRender
                      render={({ onEnter, onLeave, hovering }) => (
                        <ButtonHoverPrimary
                          onEnter={onEnter}
                          onLeave={onLeave}
                          hovering={hovering}
                          className="pano-ctrl-btn share media-ctrl-button"
                          color={primaryColor}
                          backgroundColor="#fff"
                          handleClick={closeShareModal}
                          aria-label={t('publicTour.ada.shareLabel')}
                          isMobile={isMobile}
                          tooltip={{
                            text: t('publicTour.share'),
                            node: '.pano-ctrl-btn.share',
                            position: 'bottom-center',
                          }}
                          tooltipClass={`share-tooltip  ${staticImg && 'static'}`}
                          data-cy="pano-ctrl-btn-share"
                        >
                          <i aria-hidden="true" className="material-icons">
                            share
                          </i>
                        </ButtonHoverPrimary>
                      )}
                    />
                  </li>
                )}
                {(is360Pano || isVideo360) && (
                  <React.Fragment>
                    <li>
                      <ButtonHoverRender
                        render={({ onEnter, onLeave, hovering }) => (
                          <ButtonHoverPrimary
                            onEnter={onEnter}
                            onLeave={onLeave}
                            active={panoRotating}
                            hovering={hovering}
                            className="pano-ctrl-btn rotate media-ctrl-button"
                            color={primaryColor}
                            backgroundColor="#fff"
                            handleClick={toggleAutoRotate}
                            aria-label={autoRotateAriaLabel}
                            isMobile={isMobile}
                            tooltip={{
                              text: t('publicTour.autoRotate'),
                              node: '.pano-ctrl-btn.rotate',
                              position: 'bottom-center',
                            }}
                            tooltipClass={'rotate-tooltip'}
                            data-cy="pano-ctrl-btn-rotate"
                          >
                            <i aria-hidden="true" className="material-icons">
                              {autoRotateDirection !== 'clockwise'
                                ? 'rotate_left'
                                : 'rotate_right'}
                            </i>
                          </ButtonHoverPrimary>
                        )}
                      />
                    </li>
                    {showVRMode && isMobile && (
                      <li>
                        <ButtonHoverRender
                          render={({ onEnter, onLeave, hovering }) => (
                            <ButtonHoverPrimary
                              onEnter={onEnter}
                              onLeave={onLeave}
                              hovering={hovering}
                              className="pano-ctrl-btn vr"
                              color={primaryColor}
                              backgroundColor="#fff"
                              handleClick={() => toggleVR(true)}
                              aria-label={t('publicTour.ada.vrModeLabel')}
                              isMobile={isMobile}
                              tooltip={{
                                text: t('publicTour.vrMode'),
                                node: '.pano-ctrl-btn.vr',
                                position: 'bottom',
                              }}
                              tooltipClass={'vr-tooltip'}
                              data-cy="pano-ctrl-btn-vr"
                            >
                              <VrGoggleSvg
                                fill={hovering ? '#fff' : primaryColor}
                              />
                            </ButtonHoverPrimary>
                          )}
                        />
                      </li>
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            {!isMobile && !mapView && (
              <li>
                <ButtonHoverRender
                  render={({ onEnter, onLeave, hovering }) => (
                    <ButtonHoverPrimary
                      onEnter={onEnter}
                      onLeave={onLeave}
                      hovering={hovering}
                      className="pano-ctrl-btn full-screen media-ctrl-button"
                      color={primaryColor}
                      backgroundColor="#fff"
                      handleClick={toggleFullScreen}
                      aria-label={t('publicTour.ada.fullScreenLabel')}
                      isMobile={isMobile}
                      tooltip={{
                        text: t('publicTour.fullScreen'),
                        node: '.pano-ctrl-btn.full-screen',
                        position: (is360Pano || isVideo360) ? 'bottom' : 'bottom-right',
                      }}
                      tooltipClass={`full-screen-tooltip ${(is360Pano || isVideo360) ? 'with-camera-controls' : ''}`}
                      data-cy="pano-ctrl-btn-full-screen"
                    >
                      <i className="material-icons">fullscreen</i>
                    </ButtonHoverPrimary>
                  )}
                />
              </li>
            )}
            {mapView && !isMobile && (
            <li>
              <ButtonHoverRender
                render={({ onEnter, onLeave, hovering }) => (
                  <ButtonHoverPrimary
                    onEnter={onEnter}
                    onLeave={onLeave}
                    hovering={hovering}
                    className="pano-ctrl-btn show-stops"
                    color={primaryColor}
                    backgroundColor="#fff"
                    handleClick={() =>
                      setNewMapBounds(stops, stopIds, {
                        top: 150,
                        right: 75,
                        bottom: 250,
                        left: 75,
                      })
                    }
                    aria-label={t('publicTour.ada.seeAllStops')}
                    isMobile={isMobile}
                    tooltip={{
                      text: t('publicTour.seeAllStops'),
                      node: '.pano-ctrl-btn.show-stops',
                      position: 'bottom',
                    }}
                  >
                    <i className="material-icons">zoom_out_map</i>
                  </ButtonHoverPrimary>
                )}
              />
            </li>
            )}
            <li>
              {(is360Pano || isVideo360) && (
                <CameraControls
                  primaryColor={primaryColor}
                  isOpen={cameraControlsOpen}
                  onToggle={toggleCameraControls}
                  isMobile={isMobile}
                  key={currentStopId}
                />
              )}
            </li>
          </React.Fragment>
        </ul>
      </div>


    </div>
  );
};

MainMediaControls.defaultProps = {
  normal: {
    background: '#FFF',
    text: '#009b65',
  },
  hover: {
    background: '#009b65',
    text: '#FFF',
  },
};

export default MainMediaControls;
